import { useState } from "react";
import { toast } from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Button from "@/components/loading-button";
import useStore from "@/services/store";
import api from "@/services/api";

const translations = {
  en: {
    title: "Culture Profile",
    description: "Define your culture profile through dilemmas. Used for matching, coaching, and your career page.",
    step_of: "of",
    your_example: "Give a real example from your team",
    example_placeholder: "Describe a specific situation where this happened...",
    example_min: "Minimum 20 characters",
    next: "Next",
    previous: "Previous",
    analyze: "Analyze Culture Profile",
    analyzing: "Analyzing...",
    results_title: "Your Culture Profile",
  },
  fr: {
    title: "Profil Culturel",
    description: "Definissez votre profil culturel a travers des dilemmes. Utilise pour le matching, le coaching et votre page carriere.",
    step_of: "sur",
    your_example: "Donnez un exemple concret de votre equipe",
    example_placeholder: "Decrivez une situation reelle ou c'est arrive...",
    example_min: "Minimum 20 caracteres",
    next: "Suivant",
    previous: "Precedent",
    analyze: "Analyser le profil culturel",
    analyzing: "Analyse en cours...",
    results_title: "Votre profil culturel",
  },
};

export default function CultureCreate({ onDone }) {
  const { t } = useComponentTranslation(translations);
  const { organization } = useStore();

  const dilemmas = organization?.culture_generated_dilemmas || [];

  const [step, setStep] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [answers, setAnswers] = useState(() =>
    dilemmas.map((d) => {
      const existing = (organization?.culture_dilemmas || []).find((cd) => cd.axis === d.axis);
      return existing
        ? { axis: d.axis, axis_label: d.axis_label, choice: existing.choice, example: existing.example }
        : { axis: d.axis, axis_label: d.axis_label, choice: "", example: "" };
    }),
  );

  const currentDilemma = dilemmas[step];
  const currentAnswer = answers[step];
  const canNext = currentAnswer?.choice && currentAnswer?.example?.length >= 20;

  const updateAnswer = (field, value) => {
    const updated = [...answers];
    updated[step] = { ...updated[step], [field]: value };
    setAnswers(updated);
  };

  const handleAnalyze = async () => {
    setProcessing(true);
    try {
      const { ok } = await api.put("/organization", { organization_id: organization._id, culture_dilemmas: answers });
      if (!ok) throw new Error("Failed to save answers");

      const { ok: ok2, data } = await api.post("/organization/culture-dilemmas/process", { organization_id: organization._id });
      if (!ok2) throw new Error("Failed to process culture");

      toast.success(t("results_title"));
      onDone(data);
    } catch (e) {
      console.error(e);
      toast.error(e.message);
    } finally {
      setProcessing(false);
    }
  };

  if (!currentDilemma) return null;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">{t("title")}</h1>
      <p className="text-gray-500 mb-8">{t("description")}</p>

      <div className="max-w-2xl mx-auto">
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8">
          {dilemmas.map((_, i) => (
            <div key={i} className={`h-2 flex-1 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-gray-200"}`} />
          ))}
        </div>
        <p className="text-sm text-gray-400 mb-6">
          {step + 1} {t("step_of")} {dilemmas.length} — {currentDilemma.axis_label}
        </p>

        {/* Situation */}
        <p className="text-lg font-medium text-gray-900 mb-6">{currentDilemma.situation}</p>

        {/* Choice cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {["A", "B"].map((choice) => {
            const label = choice === "A" ? currentDilemma.choice_a : currentDilemma.choice_b;
            const isSelected = currentAnswer?.choice === choice;
            return (
              <button
                key={choice}
                type="button"
                onClick={() => updateAnswer("choice", choice)}
                className={`p-5 rounded-xl border-2 text-left transition-all ${isSelected ? "border-primary bg-primary/5 shadow-sm" : "border-gray-200 hover:border-gray-300"}`}
              >
                <span className={`inline-block w-7 h-7 rounded-full text-center leading-7 text-sm font-bold mb-3 ${isSelected ? "bg-primary text-white" : "bg-gray-100 text-gray-500"}`}>{choice}</span>
                <p className={`text-sm font-medium ${isSelected ? "text-gray-900" : "text-gray-600"}`}>{label}</p>
              </button>
            );
          })}
        </div>

        {/* Example textarea */}
        <div className="mb-8">
          <label className="block text-sm font-semibold text-gray-900 mb-2">{t("your_example")}</label>
          <textarea
            value={currentAnswer?.example || ""}
            onChange={(e) => updateAnswer("example", e.target.value)}
            className="input min-h-[120px]"
            placeholder={t("example_placeholder")}
            rows={4}
          />
          {currentAnswer?.example?.length > 0 && currentAnswer.example.length < 20 && <p className="text-xs text-amber-600 mt-1">{t("example_min")}</p>}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => setStep(step - 1)} disabled={step === 0} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">
            <FiChevronLeft className="w-4 h-4" /> {t("previous")}
          </button>

          {step < dilemmas.length - 1 ? (
            <button type="button" onClick={() => setStep(step + 1)} disabled={!canNext} className="flex items-center gap-1 btn btn-primary disabled:opacity-30 disabled:cursor-not-allowed">
              {t("next")} <FiChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <Button loading={processing} onClick={handleAnalyze} disabled={!canNext} className="btn btn-primary">
              {processing ? t("analyzing") : t("analyze")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
